'use client';

import { useEffect, useRef, type RefObject } from 'react';

// Elements the browser treats as tab-stoppable. Mirrors the common
// focus-trap selector used across a11y libraries.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'button:not([disabled])',
  'textarea:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(', ');

interface UseDialogOptions {
  /** Whether the dialog is currently open/mounted. */
  open: boolean;
  /** Called when the user presses Escape — should close the dialog. */
  onClose: () => void;
  /** Optional element to focus on open instead of the first focusable child. */
  initialFocusRef?: RefObject<HTMLElement | null>;
}

/**
 * Shared modal-dialog behavior: focuses into the dialog on open, traps
 * Tab/Shift+Tab inside it, closes on Escape, and restores focus to whatever
 * was focused before the dialog opened (the trigger) when it closes.
 *
 * Attach the returned ref to the dialog panel element (the one carrying
 * role="dialog"). Does not touch mouse/click behavior — backdrop-click and
 * button onClick handlers keep working exactly as before.
 */
export function useDialog<T extends HTMLElement = HTMLDivElement>({
  open,
  onClose,
  initialFocusRef,
}: UseDialogOptions): RefObject<T | null> {
  const dialogRef = useRef<T>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  // Move focus in on open; restore focus to the trigger on close.
  useEffect(() => {
    if (!open) return;

    previouslyFocusedRef.current = document.activeElement as HTMLElement | null;

    const target =
      initialFocusRef?.current ??
      dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR) ??
      dialogRef.current;
    target?.focus();

    return () => {
      previouslyFocusedRef.current?.focus?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when open toggles
  }, [open]);

  // Escape-to-close + Tab focus trap.
  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }

      if (e.key !== 'Tab' || !dialogRef.current) return;

      const focusables = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
      );
      if (focusables.length === 0) return;

      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;

      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  return dialogRef;
}
